import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, CircleCheck, ShieldCheck, Phone, ArrowRight } from 'lucide-react';
import { SITE } from '../../data/site';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.12, ease: 'easeOut' } }),
};

// Homepage hero: displays the owner's banner artwork full-width. The artwork
// already contains the headline and a drawn CTA button, so we overlay an
// invisible link on the button area to make it clickable, plus a visible CTA
// bar on small screens where the baked-in button becomes too small to tap.
export const HomeHero = () => (
  <section className="relative bg-dark" data-testid="hero-section">
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7 }} className="relative">
      <img
        src={SITE.heroBanner}
        alt="Phoenix Roofing & Chimney - Expert Roofing & Chimney Solutions"
        className="w-full h-auto block"
        data-testid="hero-banner-image"
      />
      {/* Clickable overlay matching the banner's drawn CTA button */}
      <Link
        to="/free-estimate"
        aria-label="Request a Free Estimate"
        data-testid="hero-cta"
        className="absolute rounded-xl transition-shadow duration-300 hover:shadow-[0_0_0_3px_hsl(var(--primary-glow)/0.75)]"
        style={{ left: '3.2%', top: '68.5%', width: '29.5%', height: '9.5%' }}
      />
    </motion.div>
    {/* Mobile CTA bar (banner text is small on phones) */}
    <div className="sm:hidden bg-dark px-4 pb-6 pt-2 flex flex-col gap-3">
      <Link to="/free-estimate" className="btn-cta w-full">
        Request a Free Estimate <ArrowRight className="w-4 h-4" />
      </Link>
      <a href={SITE.phoneHref} className="btn-ghost-light w-full">
        <Phone className="w-4 h-4" /> {SITE.phone}
      </a>
    </div>
  </section>
);


export const Hero = ({ kicker = 'Top Rated Roofing Company', titleWhite = 'Phoenix', titleRed = 'Roof Repair and Replacement', sub = 'Free Inspections' }) => (
  <section className="relative flex items-center min-h-[560px] md:min-h-[640px] overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${SITE.heroImage})` }} />
    {/* Directional overlay keeps left-aligned copy readable at every stop */}
    <div className="absolute inset-0" style={{ background: 'linear-gradient(92deg, hsl(222 30% 7% / 0.92) 0%, hsl(222 30% 8% / 0.78) 42%, hsl(222 30% 10% / 0.35) 100%)' }} />

    <div className="relative z-10 w-full max-w-[1320px] mx-auto px-4 lg:px-8 py-20">
      <div className="max-w-2xl">
        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}>
          <span className="inline-flex items-center gap-2 rounded-full bg-dark-foreground/10 border border-dark-foreground/15 backdrop-blur-sm px-4 py-2 text-dark-foreground font-heading font-semibold text-[13px]" data-testid="hero-kicker">
            <span className="flex items-center gap-0.5 text-[hsl(var(--accent))]">
              {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-3.5 h-3.5 fill-current" />)}
            </span>
            {kicker}
          </span>
        </motion.div>

        <motion.h1
          variants={fadeUp} initial="hidden" animate="show" custom={1}
          className="mt-6 font-heading font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.08] text-dark-foreground"
          data-testid="hero-title"
        >
          {titleWhite} <span className="text-fire">{titleRed}</span>
        </motion.h1>

        <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2} className="mt-5 text-dark-foreground/80 text-lg max-w-lg font-body">
          Save up to 40% with your local roofing and chimney specialists. Honest pricing, quality craftsmanship, and same-day service.
        </motion.p>

        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-9 flex flex-wrap items-center gap-4">
          <Link to="/free-estimate" className="btn-cta text-[15px] !px-8" data-testid="hero-cta">
            Get a Free Estimate <ArrowRight className="w-4 h-4" />
          </Link>
          <a href={SITE.phoneHref} className="btn-ghost-light text-[15px]">
            <Phone className="w-4 h-4" /> {SITE.phone}
          </a>
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-3 text-dark-foreground/85 font-heading font-semibold text-sm" data-testid="hero-sub">
          <span className="flex items-center gap-2"><CircleCheck className="w-[18px] h-[18px] text-[hsl(var(--primary-glow))]" /> {sub}</span>
          <span className="flex items-center gap-2"><ShieldCheck className="w-[18px] h-[18px] text-[hsl(var(--primary-glow))]" /> Licensed & Insured</span>
          <span className="flex items-center gap-2"><CircleCheck className="w-[18px] h-[18px] text-[hsl(var(--primary-glow))]" /> Save up to 40%</span>
        </motion.div>
      </div>
    </div>
  </section>
);
